import { useMemo, useState } from 'react';

import type { PartnerDto } from '../../api/partners';
import { usePartnerPoolPatch } from '../../api/partners';
import type { VenueDirectoryEntry } from '../../api/venues';
import { useVenueDirectory } from '../quotas/dictionary';
import { Button, EmptyState, ErrorState, Input, Spinner } from '../../components/ui';
import {
  POOL_LOW_WARNING,
  parsePoolInput,
  poolChanged,
  poolLowWarning,
  poolUsageLabel,
} from './partner-pool-model';

/** One partner of the pool block, seeded from the venue reads (#6). */
interface PartnerRef {
  partnerId: number;
  name: string;
  /** The SHARED pool as the last venue read reported it — the block's starting number. */
  planOnline: number | null;
}

/**
 * «Онлайн-квота партнёра» — the Settings block of contract #29 (ADDITIVE v1.8.0,
 * wave 9, T.Y9, D2/A14): the ADMIN fills the partner's SHARED online pool, the
 * number every `PARTNER_ONLINE` branch shows and the prefill quota check reads.
 *
 * There is NO partner read in the contract (SR §3.6) — a partner reaches the
 * clients through #6/#7 — so the block seeds itself from the venue directory:
 * one `PARTNER_ONLINE` row per partner is enough to know its id, name and the
 * pool it currently carries. `used`, the rows already counted against the pool,
 * travels only on #29's answer, so «использовано» is honest about being unknown
 * until the first save of this session returns it.
 *
 * Lowering the pool below `used` is legal on purpose (SR §7.4 / R13): the live
 * rows stay, are matched and edited as before, and only NEW rows are refused
 * until the pool catches up. That is exactly what the warning says, and it is a
 * warning — not a refusal.
 */
export function PartnerPoolTab() {
  const directory = useVenueDirectory();
  const partners = useMemo(() => partnersOf(directory.data ?? []), [directory.data]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const partner = partners.find((entry) => entry.partnerId === selectedId) ?? partners[0];

  if (directory.isPending) {
    return (
      <div className="card" data-testid="partner-pool-skeleton">
        <div className="skeleton skeleton--line skeleton--wide" />
        <div className="skeleton skeleton--line" />
      </div>
    );
  }

  if (directory.isError) {
    return (
      <div className="card">
        <ErrorState
          message={directory.error instanceof Error ? directory.error.message : 'Не удалось загрузить партнёрские филиалы.'}
          onRetry={() => void directory.refetch()}
        />
      </div>
    );
  }

  return (
    <div className="card" data-testid="partner-pool-panel">
      <header className="status-head">
        <div>
          <h2>Онлайн-квота партнёра</h2>
          <p className="page-header__sub">
            Общий пул мест онлайн-набора партнёра — его читают все филиалы и лимит предзаполнения кабинета.
          </p>
        </div>
      </header>

      {partner === undefined ? (
        <EmptyState
          title="Партнёрских филиалов нет"
          hint="Пул появляется здесь, как только в системе есть площадка типа «ПАРТНЕР-ОНЛАЙН»."
        />
      ) : (
        <PartnerPoolForm key={partner.partnerId} partner={partner} partners={partners} onSelect={setSelectedId} />
      )}

      <p className="settings-note" data-testid="partner-pool-note">
        Изменение пула записывается в журнал аудита (ENTITY_CHANGE, со старым и новым значением). Понижение ниже
        уже занятого числа мест существующие строки не удаляет.
        {directory.isFetching ? <Spinner label="Обновляем" /> : null}
      </p>
    </div>
  );
}

function PartnerPoolForm({
  partner,
  partners,
  onSelect,
}: {
  partner: PartnerRef;
  partners: ReadonlyArray<PartnerRef>;
  onSelect: (partnerId: number) => void;
}) {
  const save = usePartnerPoolPatch();
  const [draft, setDraft] = useState(partner.planOnline === null ? '' : String(partner.planOnline));
  const [used, setUsed] = useState<number | null>(null);
  const [savedPlan, setSavedPlan] = useState<number | null>(partner.planOnline);
  const [error, setError] = useState<string | null>(null);

  const parsed = parsePoolInput(draft);
  // The warning compares the DRAFT against the last known `used`; an empty draft
  // compares against nothing and warns on nothing.
  const warning = poolLowWarning(parsed.value, used);
  const changed = poolChanged(savedPlan, parsed.value);
  const blocked = parsed.error !== undefined || !changed;

  const submit = () => {
    if (blocked || parsed.value === null) return;
    setError(null);
    save.mutate(
      { partnerId: partner.partnerId, planOnline: parsed.value },
      {
        onSuccess: (row: PartnerDto) => {
          setUsed(row.used);
          setSavedPlan(row.planOnline);
          setDraft(String(row.planOnline));
        },
        onError: (cause) => setError(cause instanceof Error ? cause.message : 'Не удалось сохранить квоту.'),
      },
    );
  };

  return (
    <div className="partner-pool" data-testid="partner-pool-form">
      {partners.length > 1 ? (
        <label className="partner-pool__partner">
          <span className="field__label">Партнёр</span>
          <select
            className="input"
            data-testid="partner-pool-select"
            value={partner.partnerId}
            onChange={(event) => onSelect(Number.parseInt(event.target.value, 10))}
          >
            {partners.map((entry) => (
              <option key={entry.partnerId} value={entry.partnerId}>
                {entry.name}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <p className="partner-pool__name" data-testid="partner-pool-name">
          {partner.name}
        </p>
      )}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <label className="partner-pool__field" htmlFor="partner-pool-input">
          <span className="field__label">Онлайн-квота (мест)</span>
          <Input
            id="partner-pool-input"
            data-testid="partner-pool-input"
            inputMode="numeric"
            value={draft}
            aria-invalid={parsed.error !== undefined}
            onChange={(event) => setDraft(event.currentTarget.value)}
          />
          <span className="user-drawer__hint" data-testid="partner-pool-usage">
            {poolUsageLabel(parsed.value, used)}
          </span>
          {parsed.error ? (
            <span className="user-role-error" role="alert" data-testid="partner-pool-error">
              {parsed.error}
            </span>
          ) : null}
          {warning ? (
            <span className="user-role-error" role="alert" data-testid="partner-pool-warning">
              {POOL_LOW_WARNING}
            </span>
          ) : null}
        </label>
        <div className="partner-pool__actions">
          <Button
            type="submit"
            variant="primary"
            size="sm"
            loading={save.isPending}
            disabled={blocked}
            data-testid="partner-pool-save"
          >
            Сохранить
          </Button>
          {error ? (
            <span className="user-role-error" role="alert" data-testid="partner-pool-request-error">
              {error}
            </span>
          ) : null}
        </div>
      </form>
    </div>
  );
}

/**
 * The partners of the directory, one row each, in id order. A partner without a
 * single `PARTNER_ONLINE` branch cannot appear here — and cannot be edited
 * through #29 either, because there is no partner dictionary to read it from.
 */
function partnersOf(entries: ReadonlyArray<VenueDirectoryEntry>): PartnerRef[] {
  const byId = new Map<number, PartnerRef>();
  for (const entry of entries) {
    if (entry.kind !== 'PARTNER_ONLINE' || entry.partnerId === null) continue;
    const known = byId.get(entry.partnerId);
    if (known === undefined) {
      byId.set(entry.partnerId, {
        partnerId: entry.partnerId,
        name: entry.partnerName ?? `Партнёр #${entry.partnerId}`,
        planOnline: entry.partnerPlanOnline,
      });
      continue;
    }
    // A branch whose read already carries a pool wins over one that does not.
    if (known.planOnline === null && entry.partnerPlanOnline !== null) {
      byId.set(entry.partnerId, { ...known, planOnline: entry.partnerPlanOnline });
    }
  }
  return [...byId.values()].sort((a, b) => a.partnerId - b.partnerId);
}
